// Phase 9a slice 1 — Chemical & Inventory page.
//
// Two sections in one page:
//   - Application log (reverse-chrono, joined to product name/category/FRAC)
//   - "Log application" form (product picker + zone + rate + area + weather)
//
// Intentionally minimal — tank mix + FRAC rotation assistant + REI/PHI
// tracker + inventory ledger are slice 2. This slice earns the module its
// nav slot and gives the superintendent something they can actually USE on
// day one of the sensor + satellite data flowing.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  Application,
  Product,
  ProductCategory,
  UserRole,
  Zone,
} from '@greeniq/shared';
import { ApiError, api } from '../api/client.js';
import {
  createApplication,
  listApplications,
  listProducts,
  signageUrl,
} from '../api/chemical.js';
import { getWeatherCurrent } from '../api/weather.js';
import { useMe } from '../context/MeContext.js';
import { ReiTrackerCard } from '../components/ReiTrackerCard.js';

const WRITE_ROLES: UserRole[] = ['ageye_admin', 'superintendent', 'assistant_super'];

const CATEGORIES: Array<{ value: ProductCategory | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'fungicide', label: 'Fungicide' },
  { value: 'herbicide', label: 'Herbicide' },
  { value: 'insecticide', label: 'Insecticide' },
  { value: 'fertilizer', label: 'Fertilizer' },
  { value: 'pgr', label: 'PGR' },
  { value: 'wetting_agent', label: 'Wetting agent' },
  { value: 'adjuvant', label: 'Adjuvant' },
  { value: 'other', label: 'Other' },
];

function extractLeadingNumber(s: string): string | null {
  const m = s.match(/[\d.]+/);
  return m ? m[0] : null;
}

export function ChemicalPage() {
  const { courseSlug } = useParams();
  const { me } = useMe();
  const [applications, setApplications] = useState<Application[] | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const role: UserRole | undefined = me?.courses.find((c) => c.courseSlug === courseSlug)?.role;
  const canWrite = role ? WRITE_ROLES.includes(role) : false;

  async function loadAll() {
    if (!courseSlug) return;
    try {
      const [apps, prods, course] = await Promise.all([
        listApplications(courseSlug, { limit: 200 }),
        listProducts(courseSlug, { limit: 500 }),
        api.get<{ zones: Zone[] }>(`/courses/${courseSlug}`),
      ]);
      setApplications(apps.applications);
      setProducts(prods.products);
      setZones(course.zones ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  useEffect(() => {
    void loadAll();
  }, [courseSlug]);

  if (error) return <div className="p-8 text-[color:var(--color-error)]">{error}</div>;

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="uppercase tracking-wider text-xs text-greeniq-400 font-semibold">Phase 9</p>
          <h1 className="mt-1 text-3xl md:text-4xl font-display font-bold text-fg">
            Chemical &amp; Inventory
          </h1>
          <p className="mt-1 text-fg-muted">
            Log every application. {products.length} products in the catalog — FRAC, IRAC, HRAC codes + REI/PHI.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link
            to={`/app/${courseSlug}/chemical/tank-mix`}
            className="px-4 py-2 rounded-md bg-bg-hover text-greeniq-300 text-sm font-semibold hover:bg-bg-elevated"
          >
            Tank Mix Calc
          </Link>
          <Link
            to={`/app/${courseSlug}/chemical/inventory`}
            className="px-4 py-2 rounded-md bg-bg-hover text-greeniq-300 text-sm font-semibold hover:bg-bg-elevated"
          >
            Inventory
          </Link>
          {role === 'ageye_admin' && (
            <Link
              to={`/app/${courseSlug}/chemical/import`}
              className="px-4 py-2 rounded-md bg-bg-hover text-greeniq-300 text-sm font-semibold hover:bg-bg-elevated"
            >
              Bulk import
            </Link>
          )}
          {canWrite && (
            <button
              onClick={() => setShowForm((v) => !v)}
              className="px-4 py-2 rounded-md bg-greeniq-500 text-[#0b1014] text-sm font-semibold hover:bg-greeniq-400"
            >
              {showForm ? 'Close form' : '+ Log application'}
            </button>
          )}
        </div>
      </div>

      {courseSlug && <ReiTrackerCard courseSlug={courseSlug} />}

      {canWrite && showForm && courseSlug && (
        <LogApplicationForm
          courseSlug={courseSlug}
          products={products}
          zones={zones}
          onSaved={() => {
            setShowForm(false);
            void loadAll();
          }}
        />
      )}

      <ApplicationLogTable
        applications={applications}
        products={products}
        zones={zones}
        courseSlug={courseSlug ?? ''}
      />
    </div>
  );
}

function ApplicationLogTable({
  applications,
  products,
  zones,
  courseSlug,
}: {
  applications: Application[] | null;
  products: Product[];
  zones: Zone[];
  courseSlug: string;
}) {
  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const zoneById = useMemo(() => new Map(zones.map((z) => [z.id, z])), [zones]);

  if (applications === null) {
    return <p className="mt-8 text-fg-muted">Loading…</p>;
  }
  if (applications.length === 0) {
    return (
      <div className="mt-8 p-8 text-center text-fg-muted bg-bg-card rounded-lg border border-dashed border-border">
        <p>No applications logged yet.</p>
        <p className="mt-2 text-sm">Log your first spray, fert hit, or PGR pass to start the season's records.</p>
      </div>
    );
  }

  return (
    <div className="mt-8 overflow-auto bg-bg-card rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-border bg-bg-elevated">
            <th className="px-3 py-2 font-semibold text-fg-muted">Date</th>
            <th className="px-3 py-2 font-semibold text-fg-muted">Product</th>
            <th className="px-3 py-2 font-semibold text-fg-muted">Cat.</th>
            <th className="px-3 py-2 font-semibold text-fg-muted">FRAC / HRAC / IRAC</th>
            <th className="px-3 py-2 font-semibold text-fg-muted">Zone</th>
            <th className="px-3 py-2 font-semibold text-fg-muted text-right">Rate</th>
            <th className="px-3 py-2 font-semibold text-fg-muted text-right">Area</th>
            <th className="px-3 py-2 font-semibold text-fg-muted">Notes</th>
            <th className="px-3 py-2 font-semibold text-fg-muted"></th>
          </tr>
        </thead>
        <tbody>
          {applications.map((a) => {
            const p = productById.get(a.productId) ?? null;
            const moaCode = p?.fracCode ?? p?.hracCode ?? p?.iracCode ?? '—';
            const zone = a.zoneId !== null ? zoneById.get(a.zoneId) : null;
            const hasRei = p?.reiHours !== null && p?.reiHours !== undefined && p.reiHours > 0;
            return (
              <tr key={a.id} className="border-b border-border last:border-b-0">
                <td className="px-3 py-2 text-fg-muted whitespace-nowrap">{fmtDate(a.appliedAt)}</td>
                <td className="px-3 py-2 font-medium text-fg">{a.productName ?? p?.name ?? `Product #${a.productId}`}</td>
                <td className="px-3 py-2 capitalize">{(a.productCategory ?? p?.category ?? '').replace(/_/g, ' ')}</td>
                <td className="px-3 py-2 text-fg-muted">{moaCode}</td>
                <td className="px-3 py-2 text-fg-muted">{zone ? zone.name : 'Course-wide'}</td>
                <td className="px-3 py-2 text-right">
                  {a.ratePer1000sqft !== null ? `${fmtNum(a.ratePer1000sqft)} ${a.rateUnit ?? ''}/M` : '—'}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {Math.round(Number(a.areaSqft)).toLocaleString()} sqft
                </td>
                <td className="px-3 py-2 text-fg-muted max-w-xs truncate">{a.notes ?? ''}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {hasRei && (
                    <a
                      href={signageUrl(courseSlug, a.id)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-greeniq-400 hover:underline"
                    >
                      Signage →
                    </a>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LogApplicationForm({
  courseSlug,
  products,
  zones,
  onSaved,
}: {
  courseSlug: string;
  products: Product[];
  zones: Zone[];
  onSaved: () => void;
}) {
  const [productId, setProductId] = useState<number | ''>('');
  const [zoneId, setZoneId] = useState<number | ''>('');
  const [appliedAt, setAppliedAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [ratePer1000, setRatePer1000] = useState('');
  const [rateUnit, setRateUnit] = useState('fl oz');
  const [areaSqft, setAreaSqft] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const selected = productId === '' ? null : products.find((p) => p.id === productId) ?? null;

  useEffect(() => {
    if (zoneId === '') return;
    const zone = zones.find((z) => z.id === zoneId);
    if (zone) setAreaSqft(String(Math.round(Number(zone.areaSqft))));
  }, [zoneId, zones]);

  useEffect(() => {
    if (selected?.typicalRatePer1000sqft) {
      const leading = extractLeadingNumber(selected.typicalRatePer1000sqft);
      if (leading) setRatePer1000(leading);
    }
  }, [selected]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (productId === '' || !areaSqft) {
      setFormError('Product and area are required.');
      return;
    }
    setSaving(true);
    try {
      let weatherSnapshot: Record<string, unknown> | undefined;
      try {
        const w = await getWeatherCurrent(courseSlug);
        weatherSnapshot = {
          tempF: w.current.tempF,
          humidityPct: w.current.relativeHumidityPct,
          windMph: w.current.windSpeedMph,
          precipIn: w.current.precipitationIn,
          capturedAt: w.current.asOf,
        };
      } catch {
        // Non-fatal — weather snapshot is best-effort.
      }

      await createApplication(courseSlug, {
        productId: Number(productId),
        zoneId: zoneId === '' ? null : Number(zoneId),
        appliedAt: new Date(appliedAt).toISOString(),
        ratePer1000sqft: ratePer1000 ? Number(ratePer1000) : undefined,
        rateUnit: ratePer1000 ? rateUnit : undefined,
        areaSqft: Number(areaSqft),
        notes: notes || undefined,
        weatherSnapshot,
      });
      onSaved();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="mt-6 p-5 bg-bg-card rounded-lg border border-border grid gap-4 md:grid-cols-2">
      <label className="block md:col-span-2">
        <span className="text-xs uppercase tracking-wider text-fg-muted">Product</span>
        <select
          value={productId}
          onChange={(e) => setProductId(e.target.value === '' ? '' : Number(e.target.value))}
          required
          className="mt-1 w-full border border-border rounded-md py-2 px-2"
        >
          <option value="">Select a product…</option>
          {CATEGORIES.filter((c) => c.value !== '').map((c) => (
            <optgroup key={c.value} label={c.label}>
              {products
                .filter((p) => p.category === c.value)
                .map((p) => {
                  const moa = p.fracCode ?? p.hracCode ?? p.iracCode;
                  return (
                    <option key={p.id} value={p.id}>
                      {p.name}{moa ? ` · ${moa}` : ''}
                    </option>
                  );
                })}
            </optgroup>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-xs uppercase tracking-wider text-fg-muted">Zone (optional — blank = course-wide)</span>
        <select
          value={zoneId}
          onChange={(e) => setZoneId(e.target.value === '' ? '' : Number(e.target.value))}
          className="mt-1 w-full border border-border rounded-md py-2 px-2"
        >
          <option value="">Course-wide</option>
          {zones.map((z) => (
            <option key={z.id} value={z.id}>
              {z.name} · {z.zoneType.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-xs uppercase tracking-wider text-fg-muted">Applied at</span>
        <input
          type="datetime-local"
          value={appliedAt}
          onChange={(e) => setAppliedAt(e.target.value)}
          className="mt-1 w-full border border-border rounded-md py-2 px-2"
        />
      </label>

      <label className="block">
        <span className="text-xs uppercase tracking-wider text-fg-muted">Rate / 1000 sqft</span>
        <div className="mt-1 flex gap-2">
          <input
            type="number"
            step="0.001"
            value={ratePer1000}
            onChange={(e) => setRatePer1000(e.target.value)}
            className="flex-1 border border-border rounded-md py-2 px-2"
            placeholder={selected?.typicalRatePer1000sqft ?? ''}
          />
          <select
            value={rateUnit}
            onChange={(e) => setRateUnit(e.target.value)}
            className="border border-border rounded-md py-2 px-2"
          >
            <option value="fl oz">fl oz</option>
            <option value="oz">oz</option>
            <option value="lb">lb</option>
            <option value="g">g</option>
          </select>
        </div>
      </label>

      <label className="block">
        <span className="text-xs uppercase tracking-wider text-fg-muted">Area (sqft)</span>
        <input
          type="number"
          step="1"
          value={areaSqft}
          onChange={(e) => setAreaSqft(e.target.value)}
          required
          className="mt-1 w-full border border-border rounded-md py-2 px-2"
        />
      </label>

      <label className="block md:col-span-2">
        <span className="text-xs uppercase tracking-wider text-fg-muted">Notes</span>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="mt-1 w-full border border-border rounded-md py-2 px-2"
          placeholder="e.g. preventative dollar spot; tank-mixed with Primo"
        />
      </label>

      {formError && <div className="md:col-span-2 text-sm text-[color:var(--color-error)]">{formError}</div>}

      <div className="md:col-span-2 flex items-center gap-3">
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 rounded-md bg-greeniq-500 text-[#0b1014] font-semibold hover:bg-greeniq-400 disabled:opacity-50"
        >
          {saving ? 'Logging…' : 'Log application'}
        </button>
        <span className="text-xs text-fg-muted">
          Weather conditions auto-capture from the on-site station on save.
        </span>
      </div>
    </form>
  );
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fmtNum(v: number | null | string): string {
  if (v === null) return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(n < 10 ? 2 : 0);
}
